import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../components/Navbar";

const STATUS_STEPS = [
  { key: "placed",     label: "Order Placed",    desc: "Your order has been received" },
  { key: "processing", label: "Processing",       desc: "We're preparing your order" },
  { key: "shipped",    label: "Shipped",          desc: "Your order is on the way" },
  { key: "delivered",  label: "Delivered",        desc: "Delivered successfully" },
];

export default function OrderConfirmation() {
  const { id }    = useParams();
  const { token } = useAuth();
  const [order,   setOrder]   = useState(null);
  const [loading, setLoading] = useState(true);

  const API = process.env.REACT_APP_API_URL || "http://localhost:5001";

  useEffect(() => {
    fetch(`${API}/api/orders/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => { setOrder(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id, token]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center pt-20">
      <div className="w-10 h-10 border-2 border-stone-200 border-t-rose-400 rounded-full animate-spin" />
    </div>
  );

  if (!order) return (
    <div className="min-h-screen flex items-center justify-center pt-20">
      <p className="text-stone-400 text-sm">Order not found.</p>
    </div>
  );

  // Match status key — handles both "placed" and legacy "confirmed"
  const normalizedStatus = order.status === "confirmed" ? "placed" : order.status;
  const currentStep = STATUS_STEPS.findIndex(s => s.key === normalizedStatus);

  return (
    <div className="min-h-screen bg-stone-50 pt-20 pb-16">
      <div className="max-w-lg mx-auto px-4">

        {/* ── Success banner ── */}
        <div className="bg-white rounded-3xl shadow-sm border border-stone-100 p-8 mb-6 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="serif text-2xl font-bold text-stone-900 mb-2">Order Confirmed!</h1>
          <p className="text-stone-500 text-sm mb-3">Thank you for your purchase. We'll get it to you soon.</p>
          <span className="inline-block bg-stone-100 text-stone-700 text-sm font-semibold px-4 py-2 rounded-full font-mono">
            #{String(order.id).padStart(6, "0")}
          </span>
        </div>

        {/* ── Order status tracker ── */}
        <div className="bg-white rounded-3xl shadow-sm border border-stone-100 p-6 mb-6">
          <h2 className="font-bold text-stone-900 mb-5">Order Status</h2>
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-4 top-4 bottom-4 w-0.5 bg-stone-100" />
            {STATUS_STEPS.map((step, i) => {
              const done   = i <= currentStep;
              const active = i === currentStep;
              return (
                <div key={step.key} className="flex items-start gap-4 pb-5 last:pb-0 relative">
                  <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center z-10 transition-colors ${
                    done ? "bg-emerald-500" : "bg-stone-100"
                  }`}>
                    {done ? (
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <div className="w-2 h-2 rounded-full bg-stone-300" />
                    )}
                  </div>
                  <div className="pt-1">
                    <p className={`text-sm font-semibold ${
                      active ? "text-emerald-600" : done ? "text-stone-900" : "text-stone-400"
                    }`}>
                      {step.label}
                    </p>
                    <p className="text-xs text-stone-400 mt-0.5">{step.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Items ordered ── */}
        {order.items?.length > 0 && (
          <div className="bg-white rounded-3xl shadow-sm border border-stone-100 p-6 mb-6">
            <h2 className="font-bold text-stone-900 mb-4">Items Ordered</h2>
            <div className="space-y-3">
              {order.items.map((item, i) => {
                // Support both img and image field names (CartContext uses img)
                const imgSrc = item.img || item.image || "";
                const qty    = item.qty || item.quantity || 1;
                return (
                  <div key={i} className="flex gap-3 items-center">
                    {imgSrc && (
                      <img src={imgSrc} alt={item.name}
                        className="w-12 h-12 rounded-xl object-cover bg-stone-100 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-stone-800 truncate">{item.name}</p>
                      <p className="text-xs text-stone-400">
                        Qty: {qty}
                        {item.selectedSize  ? ` · Size: ${item.selectedSize}`          : item.size  ? ` · Size: ${item.size}`   : ""}
                        {item.selectedColor ? ` · ${item.selectedColor}`               : ""}
                      </p>
                    </div>
                    <p className="text-sm font-bold text-stone-900 flex-shrink-0">
                      ${(item.price * qty).toLocaleString("en-IN")}
                    </p>
                  </div>
                );
              })}
            </div>
            <div className="border-t border-stone-100 mt-4 pt-4 flex justify-between">
              <span className="font-bold text-stone-900">Total Paid</span>
              <span className="serif text-xl font-bold text-stone-900">
                ${Number(order.total).toLocaleString("en-IN")}
              </span>
            </div>
          </div>
        )}

        {/* ── Delivery address ── */}
        {order.address_line1 && (
          <div className="bg-white rounded-3xl shadow-sm border border-stone-100 p-6 mb-6">
            <h2 className="font-bold text-stone-900 mb-3">Delivering To</h2>
            <p className="font-semibold text-stone-800 text-sm">{order.full_name}</p>
            <p className="text-stone-500 text-xs mt-0.5">{order.phone}</p>
            <p className="text-stone-600 text-sm mt-1">
              {order.address_line1}{order.address_line2 ? `, ${order.address_line2}` : ""}
            </p>
            <p className="text-stone-600 text-sm">
              {order.city}{order.county ? `, ${order.county}` : ""}, {order.state} {order.zip}
            </p>
            <p className="text-stone-500 text-xs mt-0.5">{order.country}</p>
          </div>
        )}

        {/* ── Actions ── */}
        <div className="flex gap-3">
          {/* Deep-links straight to the Orders tab in Settings */}
          <Link
            to="/settings"
            state={{ tab: "orders" }}
            className="flex-1 py-4 rounded-2xl border-2 border-stone-200 text-stone-700 font-semibold text-sm text-center hover:border-stone-400 transition-colors"
          >
            View My Orders
          </Link>
          <Link
            to="/shop"
            className="flex-1 py-4 rounded-2xl bg-stone-900 text-white font-semibold text-sm text-center hover:bg-rose-500 transition-all duration-300"
          >
            Continue Shopping
          </Link>
        </div>

      </div>
    </div>
  );
}